// src/pages/laboratory/ProductsPage.jsx - Updated with promotion restrictions
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { getProductImageUrl } from '../../utils/imageUrl';
import { 
  Plus, 
  Package, 
  Search, 
  Percent,
  PencilLine, 
  Trash2, 
  Eye,
  AlertCircle,
  Filter,
  SortAsc,
  SortDesc,
  Grid,
  List,
  Star,
  TrendingUp,
  DollarSign,
  Calendar,
  Tag,
  Image as ImageIcon,
  Shield
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';

// Importer les composants existants
import ModalComponent from '../../components/common/ModalComponent';
import ProductForm from '../../components/laboratory/forms/ProductForm';
import ProductViewModal from '../../components/laboratory/modals/ProductViewModal';

// Importer les constantes de catégories
import { 
  PRODUCT_CATEGORIES, 
  getCategoryLabel, 
  getCategoryIcon,
  getCategoryInfo 
} from '../../constants/product-categories';

// Importer les fonctions API
import {
  GET_LABORATORY_BY_USER,
  GET_PRODUCTS_BY_LABORATORY,
  GET_PROMOTIONS_BY_LABORATORY,
  GET_ADDS_BY_LABORATORY,
  CREATE_PRODUCT,
  UPDATE_PRODUCT,
  DELETE_PRODUCT
} from '../../api/laboratory-api';
import { authApi } from '../../api/auth-api';

const ProductsPage = () => {
  // Gestion d'état
  const [searchTerm, setSearchTerm] = useState('');
  const [priceFilter, setPriceFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Obtenir les données de l'utilisateur actuel
  const userData = authApi.getUserData();
  const userId = userData?.id;

  // Récupérer les données du laboratoire pour l'utilisateur actuel
  const { 
    data: laboratoryData, 
    loading: laboratoryLoading,
    error: laboratoryError 
  } = useQuery(GET_LABORATORY_BY_USER, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network'
  });

  const laboratory = laboratoryData?.laboratoryByUserId;

  // Récupérer les produits pour le laboratoire
  const {
    data: productsData,
    loading: productsLoading,
    error: productsError,
    refetch: refetchProducts
  } = useQuery(GET_PRODUCTS_BY_LABORATORY, {
    variables: { laboratoryId: laboratory?.id || '' },
    skip: !laboratory?.id,
    fetchPolicy: 'cache-and-network'
  });

  // Récupérer les promotions pour afficher les associations de produits
  const {
    data: promotionsData,
    loading: promotionsLoading
  } = useQuery(GET_PROMOTIONS_BY_LABORATORY, {
    variables: { laboratoryId: laboratory?.id || '' },
    skip: !laboratory?.id,
    fetchPolicy: 'cache-and-network'
  });
  
  const {
    data: addsData,
    loading: addsLoading,
    error: addsError
  } = useQuery(GET_ADDS_BY_LABORATORY, {
    variables: { laboratoryId: laboratory?.id || '' },
    skip: !laboratory?.id,
    fetchPolicy: 'cache-and-network'
  });

  // Mutations
  const [deleteProduct] = useMutation(DELETE_PRODUCT, {
    onCompleted: () => {
      toast.success('Produit supprimé avec succès');
      refetchProducts();
      setIsConfirmDeleteOpen(false);
      setProductToDelete(null);
    },
    onError: (error) => {
      toast.error(`Erreur lors de la suppression: ${error.message}`);
    }
  });

  // Obtenir les tableaux de produits et promotions
  const products = productsData?.productsByLaboratory || [];
  const promotions = promotionsData?.promotionsByLaboratory || [];
  const adds = addsData?.addsByLaboratory || [];

  // Créer une carte de productId vers promotions pour une recherche efficace
  const productPromotionsMap = React.useMemo(() => {
    const map = new Map();
    
    if (promotions && promotions.length > 0) {
      promotions.forEach(promotion => {
        if (promotion.products && promotion.products.length > 0) {
          promotion.products.forEach(product => {
            if (!map.has(product.id)) {
              map.set(product.id, []);
            }
            map.get(product.id).push({
              id: promotion.id,
              name: promotion.name,
              status: promotion.status,
              startDate: promotion.startDate,
              endDate: promotion.endDate,
              commissionAmount: promotion.commissionAmount,
              commissionPercentage: promotion.commissionPercentage
            });
          });
        }
      });
    }
    
    return map;
  }, [promotions]);

  // Helper functions for promotion restrictions
  const hasActivePromotions = (productId) => {
    const productPromotions = productPromotionsMap.get(productId) || [];
    return productPromotions.some(promo => 
      promo.status === 'IN_PROGRESS' || promo.status === 'DRAFT'
    );
  };

  const isPromotionEndedByContract = (promotion) => {
    // A promotion is ended by contract if:
    // 1. It has an end date in the past, OR
    // 2. Its status is 'CLOSED'
    const now = new Date();
    const endDate = new Date(promotion.endDate);
    return promotion.status === 'CLOSED' || endDate < now;
  };

  const canModifyProduct = (productId) => {
    const productPromotions = productPromotionsMap.get(productId) || [];
    // Can only modify if product has no active promotions
    // Active promotions are those with status 'IN_PROGRESS' or 'DRAFT'
    return !productPromotions.some(promo => 
      promo.status === 'IN_PROGRESS' || promo.status === 'DRAFT'
    );
  };

  const canDeleteProduct = (productId) => {
    const productPromotions = productPromotionsMap.get(productId) || [];
    // Can only delete if product has no promotions at all
    return productPromotions.length === 0;
  };

  const getRestrictionReason = (productId) => {
    const productPromotions = productPromotionsMap.get(productId) || [];
    const activePromotions = productPromotions.filter(promo => 
      promo.status === 'IN_PROGRESS' || promo.status === 'DRAFT'
    );
    
    if (activePromotions.length > 0) {
      return `Ce produit est associé à ${activePromotions.length} promotion(s) active(s). Vous devez d'abord terminer ou supprimer ces promotions.`;
    }
    
    if (productPromotions.length > 0) {
      return `Ce produit est associé à des promotions. Seuls les produits sans promotions peuvent être supprimés.`;
    }
    
    return '';
  };

  // Filtrer et trier les produits
  const filteredProducts = products
    .filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      let matchesPrice = true;
      if (priceFilter === 'low') {
        matchesPrice = product.price <= 100;
      } else if (priceFilter === 'medium') {
        matchesPrice = product.price > 100 && product.price <= 500;
      } else if (priceFilter === 'high') {
        matchesPrice = product.price > 500;
      }
      
      let matchesCategory = true;
      if (categoryFilter !== 'all') {
        matchesCategory = product.category === categoryFilter;
      }
      
      return matchesSearch && matchesPrice && matchesCategory;
    })
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (sortBy === 'price') {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortOrder === 'desc') {
        return bVal > aVal ? 1 : -1;
      } else {
        return aVal > bVal ? 1 : -1;
      }
    });

  // Fonctions utilitaires
  const getProductStats = () => {
    const totalProducts = products.length;
    const productsWithPromotions = products.filter(p => productPromotionsMap.has(p.id)).length;
    const productsWithActivePromotions = products.filter(p => hasActivePromotions(p.id)).length;
    const averagePrice = products.length > 0 
      ? products.reduce((sum, p) => sum + p.price, 0) / products.length 
      : 0;
    const totalValue = products.reduce((sum, p) => sum + p.price, 0);

    // Statistiques par catégorie
    const categoryStats = {};
    products.forEach(product => {
      if (product.category) {
        if (!categoryStats[product.category]) {
          categoryStats[product.category] = 0;
        }
        categoryStats[product.category]++;
      }
    });

    return {
      totalProducts,
      productsWithPromotions,
      productsWithActivePromotions,
      averagePrice,
      totalValue,
      categoryStats
    };
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
      minimumFractionDigits: 2
    }).format(price);
  };

  // Gestionnaires d'événements
  const handleCreateProduct = () => {
    setSelectedProduct(null);
    setIsProductFormOpen(true);
  };

  const handleEditProduct = (product) => {
    setIsViewModalOpen(false);
    setSelectedProduct(product);
    setIsProductFormOpen(true);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setIsViewModalOpen(true);
  };

  const handleDeleteProduct = (product) => {
    if (!canDeleteProduct(product.id)) {
      toast.warning(getRestrictionReason(product.id));
      return;
    }
    setProductToDelete(product);
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (productToDelete) {
      deleteProduct({
        variables: { id: productToDelete.id }
      });
    }
  };

  const handleProductSuccess = () => {
    setIsProductFormOpen(false);
    setSelectedProduct(null);
    refetchProducts();
  };

  const handleCopyProductLink = (product) => {
    const productUrl = `${window.location.origin}/product/${product.id}`;
    navigator.clipboard.writeText(productUrl);
    toast.success('Lien du produit copié dans le presse-papiers');
  };

  // Composant Carte de Produit
  const ProductCard = ({ product }) => {
    const productPromotions = productPromotionsMap.get(product.id) || [];
    const hasActivePromotion = productPromotions.some(p => p.status === 'IN_PROGRESS' || p.status === 'DRAFT');
    const canModify = canModifyProduct(product.id);
    const canDelete = canDeleteProduct(product.id);

    return (
      <div className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow group ${
        !canModify ? 'border-amber-200 bg-amber-50' : 'border-gray-200'
      }`}>
        {/* Image du produit */}
        <div className="relative aspect-square bg-gray-100 rounded-t-lg overflow-hidden">
          {product.image ? (
            <img
              src={getProductImageUrl(product.image)}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="h-16 w-16 text-gray-400" />
            </div>
          )}

          {/* Badge de catégorie */}
          {product.category && (
            <div className="absolute top-2 right-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-200 text-blue-800">
                {getCategoryIcon(product.category)}
              </span>
            </div>
          )}

          {/* Superposition d'actions */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex space-x-2">
              <button
                onClick={() => handleViewProduct(product)}
                className="p-2 bg-white rounded-full text-gray-700 hover:text-blue-600 transition-colors"
                title="Voir les détails"
              >
                <Eye size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Informations du produit */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 
              className="font-medium text-gray-900 line-clamp-2 cursor-pointer hover:text-blue-600"
              onClick={() => handleViewProduct(product)}
            >
              {product.name}
            </h3>
          </div>

          {/* Message de restriction */}
          {!canModify && (
            <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-center">
                <Shield size={14} className="text-amber-600 mr-2" />
                <p className="text-xs text-amber-800">
                  Protégé par promotion active
                </p>
              </div>
            </div>
          )}

          {/* Catégorie et sous-catégorie */}
          {product.category && (
            <div className="mb-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                {getCategoryIcon(product.category)} {getCategoryLabel(product.category)}
              </span>
              {product.subcategory && (
                <p className="text-xs text-gray-500 mt-1">{product.subcategory}</p>
              )}
            </div>
          )}

          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
            {product.description}
          </p>

          <div className="flex items-center justify-between mb-3">
            <span className="text-lg font-bold text-green-600">
              {formatPrice(product.price)}
            </span>
            <span className="text-xs text-gray-500">
              {format(new Date(product.createdAt), 'dd/MM/yyyy', { locale: fr })}
            </span>
          </div>

          {/* Informations sur les promotions */}
          {productPromotions.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {productPromotions.slice(0, 2).map(promo => (
                <span 
                  key={promo.id}
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    promo.status === 'IN_PROGRESS' 
                      ? 'bg-green-100 text-green-800' 
                      : promo.status === 'DRAFT'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                  }`}
                >
                  {promo.name}
                </span>
              ))}
              {productPromotions.length > 2 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  +{productPromotions.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // États de chargement et d'erreur
  if (laboratoryLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-700 border-r-transparent"></div>
        <p className="ml-3 text-gray-600">Chargement de votre laboratoire...</p>
      </div>
    );
  }

  if (laboratoryError || !laboratory) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Erreur</h3>
            <p className="text-red-700">
              {laboratoryError ? laboratoryError.message : 'Laboratoire non trouvé. Contactez l\'administrateur.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const stats = getProductStats();

  return (
    <div className="space-y-6">
      {/* En-tête de page */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-600 text-white p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center min-w-0">
            <Package className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold">Mes Produits</h1>
              <p className="text-blue-100 text-sm truncate hidden sm:block">
                Gérez votre catalogue de produits - {laboratory.nom}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-blue-100">Total</p>
              <p className="text-xl font-bold">{stats.totalProducts}</p>
            </div>
            <button
              onClick={handleCreateProduct}
              className="flex items-center px-3 py-2 text-sm bg-white text-blue-700 rounded-md hover:bg-blue-50 transition shadow-sm"
            >
              <Plus size={16} className="mr-1.5" />
              <span className="hidden sm:inline">Nouveau produit</span>
              <span className="sm:hidden">Nouveau</span>
            </button>
          </div>
        </div>
      </div>

      {/* Notice de restrictions */}
      {stats.productsWithActivePromotions > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-amber-800">Produits protégés</h3>
              <p className="text-sm text-amber-700">
                {stats.productsWithActivePromotions} produit(s) avec promotions actives ne peuvent pas être modifiés ou supprimés.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistiques rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Produits</p>
              <p className="text-2xl font-bold text-blue-700">{stats.totalProducts}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <Tag className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Avec Promotions</p>
              <p className="text-2xl font-bold text-green-700">{stats.productsWithPromotions}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-amber-500">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-amber-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Protégés</p>
              <p className="text-2xl font-bold text-amber-700">{stats.productsWithActivePromotions}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Prix Moyen</p>
              <p className="text-2xl font-bold text-purple-700">
                {formatPrice(stats.averagePrice)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4 lg:items-center">
          {/* Recherche */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Filtre par catégorie */}
          <div className="relative w-full lg:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Tag className="h-5 w-5 text-gray-400" />
            </div>
            <select
              className="block w-full pl-10 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">Toutes les catégories</option>
              {PRODUCT_CATEGORIES.map(category => (
                <option key={category.key} value={category.key}>
                  {category.icon} {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre par prix */}
          <div className="relative w-full lg:w-48">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <select
              className="block w-full pl-10 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={priceFilter}
              onChange={(e) => setPriceFilter(e.target.value)}
            >
              <option value="all">Tous les prix</option>
              <option value="low">Moins de 100 DH</option>
              <option value="medium">100 - 500 DH</option>
              <option value="high">Plus de 500 DH</option>
            </select>
          </div>

          {/* Tri */}
          <div className="flex space-x-2">
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="createdAt">Date de création</option>
              <option value="updatedAt">Dernière modification</option>
              <option value="name">Nom</option>
              <option value="price">Prix</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              title={`Trier par ordre ${sortOrder === 'asc' ? 'décroissant' : 'croissant'}`}
            >
              {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
            </button>
          </div>

        </div>
      </div>

      {/* Affichage des produits */}
      {productsLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-700 border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Chargement des produits...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || priceFilter !== 'all' || categoryFilter !== 'all' ? 'Aucun produit trouvé' : 'Aucun produit créé'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || priceFilter !== 'all' || categoryFilter !== 'all' 
              ? "Modifiez vos critères de recherche pour voir plus de résultats."
              : "Commencez par ajouter des produits à votre catalogue."}
          </p>
          {(!searchTerm && priceFilter === 'all' && categoryFilter === 'all') && (
            <button
              onClick={handleCreateProduct}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="-ml-1 mr-2 h-5 w-5" />
              Créer mon premier produit
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prix
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catégorie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Promotions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date création
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map(product => {
                  const productPromotions = productPromotionsMap.get(product.id) || [];
                  
                  return (
                    <tr key={product.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewProduct(product)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12 bg-gray-100 rounded-md overflow-hidden">
                            {product.image ? (
                              <img
                                src={getProductImageUrl(product.image)}
                                alt={product.name}
                                className="h-12 w-12 object-cover"
                              />
                            ) : (
                              <div className="h-12 w-12 flex items-center justify-center">
                                <Package className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                            <div className="text-sm text-gray-500 line-clamp-1">{product.description}</div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-green-600">
                          {formatPrice(product.price)}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.category ? (
                          <div>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {getCategoryIcon(product.category)} {getCategoryLabel(product.category)}
                            </span>
                            {product.subcategory && (
                              <div className="text-xs text-gray-500 mt-1">{product.subcategory}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Non catégorisé</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {productPromotions.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {productPromotions.slice(0, 2).map(promo => (
                              <span 
                                key={promo.id}
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  promo.status === 'IN_PROGRESS' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {promo.name}
                              </span>
                            ))}
                            {productPromotions.length > 2 && (
                              <span className="text-xs text-gray-500">
                                +{productPromotions.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Aucune</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(product.createdAt), 'dd/MM/yyyy', { locale: fr })}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div 
                          className="flex items-center justify-end space-x-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => handleViewProduct(product)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Voir les détails"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEditProduct(product)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Modifier"
                          >
                            <PencilLine size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de formulaire de produit */}
      {isProductFormOpen && (
        <ProductForm
          laboratory={laboratory}
          product={selectedProduct}
          restrictedEdit={selectedProduct ? hasActivePromotions(selectedProduct.id) : false}
          isOpen={isProductFormOpen}
          onClose={() => setIsProductFormOpen(false)}
          onSuccess={handleProductSuccess}
        />
      )}

      {/* Modal de vue produit */}
      {isViewModalOpen && selectedProduct && (
  <ProductViewModal
    product={selectedProduct}
    isOpen={isViewModalOpen}
    onClose={() => setIsViewModalOpen(false)}
    onEdit={handleEditProduct}
    laboratory={laboratory}
    promotions={promotions}
    adds={adds}
    fetchPromotions={false} // ADD THIS LINE - tells modal not to fetch since we're providing data
  />
)}

      {/* Modal de confirmation de suppression */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        title="Confirmer la suppression"
        onSubmit={handleConfirmDelete}
        submitLabel="Supprimer"
        submitButtonClassName="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle size={24} />
            <h3 className="text-lg font-medium">Attention</h3>
          </div>
          
          <p className="text-gray-700">
            Êtes-vous sûr de vouloir supprimer le produit <strong>{productToDelete?.name}</strong> ?
          </p>
          
          <div className="bg-yellow-50 p-3 rounded border border-yellow-100">
            <p className="text-sm text-yellow-800">
              Cette action est irréversible. Le produit sera également retiré de toutes les promotions associées.
            </p>
          </div>
          
          {productToDelete && productPromotionsMap.has(productToDelete.id) && (
            <div className="bg-blue-50 p-3 rounded border border-blue-100">
              <p className="text-sm text-blue-800">
                Ce produit est associé à {productPromotionsMap.get(productToDelete.id).length} promotion(s).
              </p>
            </div>
          )}
        </div>
      </ModalComponent>
    </div>
  );
};

export default ProductsPage;